import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useApi, useAction } from '../lib/hooks.js';
import { Loading, ErrorNote, Modal, Pill, formatQty, typedQty } from '../components/ui.jsx';
import ItemPicker from '../components/ItemPicker.jsx';

// The sign goes before the currency symbol: a refund line is "-$1.50", because
// "$-1.50" reads as a typo.
const money = (cents) => (cents == null ? '' : `${cents < 0 ? '−' : ''}$${Math.abs(cents / 100).toFixed(2)}`);

const percent = (score) => (score == null ? null : `${Math.round(score * 100)}%`);

/**
 * Req #1 / R4: triage what the scan read, then confirm it.
 *
 * This screen used to be one flat list in receipt order with a single Confirm
 * button under it, which is a confirmation dialog wearing a list's clothes: the
 * three lines worth reading sat between twenty that were not, the confidence
 * number existed on every row and was shown on none, and correcting anything
 * meant opening a modal. The default is still one tap -- that is right for a
 * clean receipt -- but the doubtful lines now come first, say how sure the
 * match is, and can be answered where they sit.
 *
 * The buckets and their order come from the server (`line.review`), not from
 * arithmetic here: AUTO_ACCEPT is the matcher's number and a second copy in
 * JSX is how the two come to disagree.
 */
export default function ReceiptReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const receipt = useApi(`/receipts/${id}`);
  const locations = useApi('/locations');
  const [editingLine, setEditingLine] = useState(null);
  const [addToInventory, setAddToInventory] = useState(true);
  const [locationId, setLocationId] = useState('');
  // M3: what the commit could not decide by itself, held here between saving
  // the receipt and leaving the screen.
  const [replaced, setReplaced] = useState(null);
  const [finished, setFinished] = useState({});
  const { busy, error, run } = useAction();

  if (receipt.loading && !receipt.data) return <Loading rows={5} />;
  if (receipt.error) return <ErrorNote error={receipt.error} onRetry={receipt.reload} />;
  if (!receipt.data) return null;

  const data = receipt.data;
  const lines = data.lines || [];
  const counts = data.counts || { suggested: 0, new: 0, recognised: 0, excluded: 0 };
  const included = lines.filter((line) => !line.excluded);
  const needsLook = lines.filter((line) => line.review === 'suggested' || line.review === 'new');
  const recognised = lines.filter((line) => line.review === 'recognised');
  const excluded = lines.filter((line) => line.review === 'excluded');
  const committed = data.status === 'committed';
  const reconciliation = data.reconciliation;
  const showReconciliation = reconciliation
    && reconciliation.status !== 'matches' && reconciliation.status !== 'unknown';

  // What a `new` line is about to create, which is the model's reading of the
  // shorthand rather than the shorthand itself -- and the one thing worth
  // seeing before a master item exists forever.
  const interpreted = new Map(
    (data.extraction?.items || []).map((item) => [item.raw_text, item.interpreted_name]),
  );

  const leave = (flash) => navigate(addToInventory ? '/inventory' : '/items', { state: { flash } });

  const commit = () => run(async () => {
    const result = await api.post(`/receipts/${id}/commit`, {
      add_to_inventory: addToInventory,
      location_id: locationId || undefined,
    });
    // M3. Buying something again usually means the last one is gone, and this
    // is the only moment the household is thinking about that food. The sheet
    // is one question with the answer already filled in; anything longer than
    // five seconds would be worse than the duplicate rows it prevents.
    const entries = result.replenishment || [];
    if (entries.length) {
      setFinished(Object.fromEntries(entries.flatMap((entry) => entry.candidates.map(
        (candidate) => [candidate.inventory_item_id, candidate.suggestion === 'close'],
      ))));
      setReplaced({ entries, flash: result });
      return;
    }
    leave(result);
  });

  const closeReplaced = () => run(async () => {
    const ids = Object.keys(finished).filter((rowId) => finished[rowId]);
    if (ids.length) await api.post(`/receipts/${id}/close-replaced`, { inventory_item_ids: ids });
    leave(replaced.flash);
  });

  const patchLine = (line, body) => run(async () => {
    await api.patch(`/receipts/${id}/lines/${line.id}`, body);
    receipt.reload();
  });

  const toggleExclude = (line) => patchLine(line, { excluded: !line.excluded });
  // "Yes, that one" and "no, not that one". The second is the half the alias
  // table never had: without it the same wrong guess is offered every month and
  // the only way to stop being asked is to accept it.
  const accept = (line) => patchLine(line, { master_item_id: line.top_suggestion.id });
  const reject = (line) => patchLine(line, { reject_master_item_id: line.top_suggestion.id });

  const reparse = () => {
    // Asked first: re-reading rebuilds every line from the photo, which throws
    // away matches and exclusions done by hand -- and it spends a photo scan.
    if (!window.confirm(
      'Re-read the photo? Every line is replaced — including any you have '
      + 'matched or excluded by hand — and it uses one photo scan.',
    )) return;
    run(async () => {
      await api.post(`/receipts/${id}/reparse`, {});
      receipt.reload();
    });
  };

  const rowProps = {
    committed,
    busy,
    interpreted,
    onEdit: setEditingLine,
    onExclude: toggleExclude,
    onAccept: accept,
    onReject: reject,
  };

  return (
    <div className="stack">
      <ErrorNote error={error} />

      {data.notes && (
        <div className="alert warn">
          <strong>The scan wasn&rsquo;t fully clear.</strong>
          <div className="small" style={{ marginTop: 4 }}>{data.notes}</div>
        </div>
      )}

      <div className="card">
        <div className="row-between">
          <div>
            <h2>{data.store_name || 'Unknown store'}</h2>
            <div className="small muted">{data.purchased_at}</div>
          </div>
          <div className="center">
            <div style={{ fontSize: '1.2rem', fontWeight: 620 }}>{money(data.total_cents)}</div>
            <div className="tiny muted">{included.length} items</div>
          </div>
        </div>
        {data.image_url && (
          <details style={{ marginTop: 12 }}>
            <summary className="small muted">View photo</summary>
            <img src={data.image_url} alt="Receipt" className="thumb-lg" style={{ height: 'auto', marginTop: 10 }} />
          </details>
        )}
      </div>

      {/* R3. The model already reported per-line prices and the subtotal, and
          until now nothing compared them. A gap wider than rounding is a line
          read twice, missed, or misread -- and knowing the direction is most of
          the way to finding which. */}
      {showReconciliation && (
        <div className="alert warn" role="status">
          <span className="small">{reconciliation.hint}</span>
        </div>
      )}

      {committed && <div className="alert info">This receipt has already been committed.</div>}

      {needsLook.length > 0 && (
        <>
          <div className="section-title">
            <h2>Needs a look</h2>
            <span className="count">{needsLook.length} of {lines.length}</span>
          </div>
          <div className="card list" style={{ borderColor: 'var(--warn)' }}>
            {needsLook.map((line) => (
              <LineRow key={line.id} line={line} {...rowProps} />
            ))}
          </div>
        </>
      )}

      {recognised.length > 0 && (
        <details className="card" style={{ marginTop: 12 }}>
          <summary className="small muted">
            {counts.recognised} line{counts.recognised === 1 ? '' : 's'} recognised
          </summary>
          <div className="list" style={{ marginTop: 6 }}>
            {recognised.map((line) => (
              <LineRow key={line.id} line={line} {...rowProps} />
            ))}
          </div>
        </details>
      )}

      {excluded.length > 0 && (
        <>
          <div className="section-title">
            <h2>Not going in</h2>
            <span className="count">{excluded.length}</span>
          </div>
          <div className="card list">
            {excluded.map((line) => (
              <LineRow key={line.id} line={line} {...rowProps} />
            ))}
          </div>
        </>
      )}

      {!committed && (
        <div className="card stack">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={addToInventory}
              onChange={(event) => setAddToInventory(event.target.checked)}
            />
            <span>Add these to my inventory</span>
          </label>

          {addToInventory && (
            <label>
              <span className="label-text">Put them in</span>
              <select value={locationId} onChange={(event) => setLocationId(event.target.value)}>
                <option value="">Each item&rsquo;s default location</option>
                {(locations.data || []).map((location) => (
                  <option key={location.id} value={location.id}>{location.name}</option>
                ))}
              </select>
            </label>
          )}

          {/* Still accept-all, and the label says what is being accepted
              without changing that: the count in brackets is the reason to
              scroll up, not a gate on the button. */}
          <button type="button" className="primary full" disabled={busy} onClick={commit}>
            {busy
              ? <><span className="spinner" /> Saving</>
              : `Confirm ${included.length} items${needsLook.length ? ` (${needsLook.length} to review)` : ''}`}
          </button>
          <button type="button" className="small ghost full" disabled={busy} onClick={reparse}>
            Re-read the photo
          </button>
        </div>
      )}

      {replaced && (
        <Modal title="You bought these again" onClose={() => leave(replaced.flash)}>
          <div className="stack">
            <ErrorNote error={error} />
            <p className="small muted" style={{ margin: 0 }}>
              Is the old one finished? Anything you leave unticked stays in the pantry.
            </p>
            <div className="card list">
              {replaced.entries.flatMap((entry) => entry.candidates.map((candidate) => (
                <label className="checkbox list-item" key={candidate.inventory_item_id}>
                  <input
                    type="checkbox"
                    checked={Boolean(finished[candidate.inventory_item_id])}
                    onChange={(event) => setFinished((previous) => ({
                      ...previous, [candidate.inventory_item_id]: event.target.checked,
                    }))}
                  />
                  <span className="grow" style={{ minWidth: 0 }}>
                    <span className="truncate" style={{ display: 'block' }}>
                      {entry.name} · {formatQty(candidate.quantity, candidate.unit)}
                    </span>
                    <span className="tiny muted truncate" style={{ display: 'block' }}>
                      {candidate.location_name ? `${candidate.location_name} · ` : ''}{candidate.why}
                    </span>
                  </span>
                </label>
              )))}
            </div>
            <button type="button" className="primary full" disabled={busy} onClick={closeReplaced}>
              {busy ? <><span className="spinner" /> Saving</> : 'Done'}
            </button>
            <button type="button" className="small ghost full" onClick={() => leave(replaced.flash)}>
              Skip
            </button>
          </div>
        </Modal>
      )}

      {editingLine && (
        <EditLine
          receiptId={id}
          line={editingLine}
          onClose={() => setEditingLine(null)}
          onSaved={() => { setEditingLine(null); receipt.reload(); }}
        />
      )}
    </div>
  );
}

/**
 * One receipt line, in whichever pile the server put it.
 *
 * The whole row still opens the full edit modal, because renaming, re-pricing
 * and re-uniting a line all live there. What is new is that the single most
 * likely answer is offered without opening anything: two 40px targets on the
 * row itself, which is the difference between correcting three lines and
 * confirming twenty-three blind.
 */
function LineRow({ line, committed, busy, interpreted, onEdit, onExclude, onAccept, onReject }) {
  const suggestion = line.top_suggestion;
  const offer = !committed && (line.review === 'suggested' || line.review === 'new') && suggestion;

  return (
    <div className="list-item" style={{ alignItems: 'flex-start' }}>
      <span className="grow" style={{ minWidth: 0 }}>
        <button
          type="button"
          className="ghost grow"
          style={{ justifyContent: 'flex-start', textAlign: 'left', padding: 0, minHeight: 0, width: '100%' }}
          onClick={() => !committed && onEdit(line)}
          disabled={committed}
        >
          <span className="grow" style={{ minWidth: 0 }}>
            <span className={`truncate ${line.excluded ? 'strike' : ''}`} style={{ display: 'block' }}>
              {line.matched_name || line.raw_text}
            </span>
            <span className="small muted truncate" style={{ display: 'block' }}>
              {/* The printed item code, when the line carried one. It is the
                  key the match was made on, so seeing it is how somebody
                  understands why a line matched a name that looks nothing
                  like the shorthand beside it. */}
              <span className="mono">
                {line.item_code ? `${line.item_code} · ` : ''}{line.raw_text}
              </span>
              {' · '}{formatQty(line.quantity, line.unit)}
              {line.total_price_cents != null && ` · ${money(line.total_price_cents)}`}
            </span>
          </span>
        </button>

        {line.review === 'new' && (
          <span className="tiny muted truncate" style={{ display: 'block', marginTop: 3 }}>
            will create {interpreted.get(line.raw_text) || line.raw_text}
          </span>
        )}

        {offer && (
          <span className="row" style={{ marginTop: 6, gap: 6 }}>
            <span className="small truncate grow">
              {line.master_item_id ? 'Matched to' : 'Looks like'}{' '}
              <strong>{suggestion.name}</strong>
              {suggestion.score != null && <span className="tiny muted"> {percent(suggestion.score)}</span>}
            </span>
            <button
              type="button"
              className="icon ghost"
              aria-label={`Yes, this line is ${suggestion.name}`}
              disabled={busy}
              onClick={() => onAccept(line)}
            >
              ✓
            </button>
            <button
              type="button"
              className="icon ghost"
              aria-label={`No, this line is not ${suggestion.name}`}
              disabled={busy}
              onClick={() => onReject(line)}
            >
              ✗
            </button>
          </span>
        )}
      </span>

      {line.review === 'new' && <Pill tone="warn">new</Pill>}
      {line.review === 'recognised' && <Pill tone="ok">known</Pill>}
      {!committed && (
        <button
          type="button"
          className="icon ghost"
          aria-label={line.excluded ? 'Include line' : 'Exclude line'}
          disabled={busy}
          onClick={() => onExclude(line)}
        >
          {line.excluded ? '↺' : '✕'}
        </button>
      )}
    </div>
  );
}

function EditLine({ receiptId, line, onClose, onSaved }) {
  const [item, setItem] = useState(
    line.master_item_id ? { id: line.master_item_id, name: line.matched_name, brand: line.matched_brand } : null,
  );
  const [quantity, setQuantity] = useState(line.quantity);
  const [unit, setUnit] = useState(line.unit);
  const { busy, error, run } = useAction();

  const save = () => run(async () => {
    await api.patch(`/receipts/${receiptId}/lines/${line.id}`, {
      master_item_id: item?.id ?? null,
      // typedQty: a blanked field means "leave it", never Number('') === 0.
      quantity: typedQty(quantity),
      unit,
    });
    onSaved();
  });

  return (
    <Modal title="Match this line" onClose={onClose}>
      <div className="stack">
        <ErrorNote error={error} />

        <div className="card" style={{ background: 'var(--surface-2)' }}>
          <div className="tiny muted">Printed on the receipt</div>
          <div className="mono">{line.raw_text}</div>
        </div>

        <label>
          <span className="label-text">Is this…</span>
          <ItemPicker value={item} onChange={setItem} autoFocus={!item} />
        </label>

        {line.suggestions?.length > 0 && !item && (
          <div>
            <div className="label-text">Close matches</div>
            <div className="chips">
              {line.suggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  type="button"
                  className="chip"
                  onClick={() => setItem(suggestion)}
                >
                  {suggestion.name} <span className="tiny">{percent(suggestion.score)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="field-row">
          <label>
            <span className="label-text">Quantity</span>
            <input type="number" inputMode="decimal" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </label>
          <label>
            <span className="label-text">Unit</span>
            <input value={unit} onChange={(e) => setUnit(e.target.value)} />
          </label>
        </div>

        <p className="tiny muted" style={{ margin: 0 }}>
          Confirming teaches the app this store&rsquo;s shorthand, so the next receipt matches it automatically.
        </p>

        <button type="button" className="primary full" disabled={busy} onClick={save}>
          {busy ? <><span className="spinner" /> Saving</> : 'Save'}
        </button>
      </div>
    </Modal>
  );
}
